import { AudioPlayer } from "../ui/AudioPlayer";
import AudioTrackInfo from "./AudioTrackInfo";
import {
  useAudioContext,
  type AudioMediaDataModel,
} from "../../domains/app/contexts/audio.context";
import { useAudioWaveform } from "../../domains/audio/hooks/useAudioWaveform";
import { useCurrentUser } from "../../domains/user/hooks/useCurrentUser";
import { isInternalRoom } from "../../lib/helpers";
import { AnalyticsItemList } from "../../tools/analytics/analytics.types";
import { gaEventArticleViewOriginalPaper } from "../../tools/analytics/domains/article.analytics";
import { gaEventPodcastEpisodeAccessOriginalPublication } from "../../tools/analytics/domains/podcast.analytics";
import { formatTime } from "../../tools/utils";

export const SPEED_RATES = [1, 1.25, 1.5, 2] as const;

/** Subtitle under the track title: journal for articles, collection otherwise. */
export function deriveTrackSource(
  media: AudioMediaDataModel | null,
  collectionName: string
): string {
  if (media === null) return collectionName;
  switch (media.type) {
    case "article_audio":
      return media.source.journal === null ? "" : media.source.journal.name;
    case "podcast_episode":
    case "playlist_audio_content":
      return collectionName;
    default:
      throw new Error(media satisfies never);
  }
}

export default function MiniAudioPlayer() {
  const {
    hasTrack,
    isPlaying,
    progressRate,
    canSeek,
    trackTitle,
    trackCollectionName,
    currentMediaContent,
    currentTime,
    duration,
    playbackRate,
    isAudioPlayerPanelOpen,
    miniPlayerStyles,
    togglePlayPause,
    seek,
    skipBackward,
    skipForward,
    setPlaybackRate,
    clear,
    setAudioPlayerPanelOpen,
    isInfoPanelVisible,
    setInfoPanelVisible,
  } = useAudioContext();
  const { currentUser } = useCurrentUser();
  // A live stream is still being synthesized server-side: no total length, so
  // the scrubber, the skip buttons and the duration label wait for the file.
  const isLiveStream = !canSeek;
  const waveform = useAudioWaveform(
    currentMediaContent === null ? null : currentMediaContent.trackUrl
  );

  if (!hasTrack) return null;

  const cycleSpeed = () => {
    const idx = SPEED_RATES.findIndex((rate) => rate === playbackRate);
    const next = SPEED_RATES[(idx === -1 ? 0 : idx + 1) % SPEED_RATES.length];
    if (next !== undefined) setPlaybackRate(next);
  };

  // Original-publication analytics: the publication info lives inline in the
  // player.
  function handleExternalClick(url: string) {
    if (currentMediaContent === null) return;
    switch (currentMediaContent.type) {
      case "article_audio":
        gaEventArticleViewOriginalPaper({
          article: currentMediaContent.source,
          listName: AnalyticsItemList.ARTICLE_PAGE,
        });
        return;
      case "podcast_episode":
        gaEventPodcastEpisodeAccessOriginalPublication({
          episode: currentMediaContent.source,
          listName: AnalyticsItemList.ROOM_PODCAST,
          publicationUrl: url,
          internal: isInternalRoom(currentUser, ""),
        });
        return;
      case "playlist_audio_content":
        return;
      default:
        throw new Error(currentMediaContent satisfies never);
    }
  }

  return (
    <div
      className="fixed inset-x-0  bottom-[calc(var(--safe-area-bottom)+16px)] z-50 mx-5 mb-[15px] animate-[slideIn_0.2s_ease-in] md:left-1/2 md:right-auto md:translate-x-[calc(-50%-20px)] md:w-[560px]"
      style={{
        transition: "bottom 0.3s ease-in-out",
        ...miniPlayerStyles,
      }}
    >
      <AudioPlayer
        title={trackTitle}
        source={deriveTrackSource(currentMediaContent, trackCollectionName)}
        imageUrl={
          currentMediaContent === null || currentMediaContent.imageUrl === ""
            ? null
            : currentMediaContent.imageUrl
        }
        state={isPlaying ? "playing" : "paused"}
        progress={progressRate === null ? null : progressRate / 100}
        waveform={waveform}
        onPlayPause={togglePlayPause}
        onClose={clear}
        onExpand={() => {
          setAudioPlayerPanelOpen(true);
        }}
        expanded={isAudioPlayerPanelOpen}
        onCollapse={() => {
          setAudioPlayerPanelOpen(false);
          setInfoPanelVisible(false);
        }}
        info={
          currentMediaContent === null ? null : (
            <AudioTrackInfo
              media={currentMediaContent}
              onExternalClick={handleExternalClick}
            />
          )
        }
        infoOpen={isInfoPanelVisible}
        onToggleInfo={() => {
          setInfoPanelVisible(!isInfoPanelVisible);
        }}
        currentLabel={formatTime(currentTime)}
        durationLabel={isLiveStream ? "--:--" : formatTime(duration)}
        onSeek={(fraction) => {
          // Nothing to seek within: the rest of a live stream does not exist
          // yet. Without this, every scrub resolves to `fraction * 0` and
          // restarts the track.
          if (isLiveStream) return;
          seek(fraction * duration);
        }}
        onSkipBack={
          isLiveStream
            ? null
            : () => {
                skipBackward(15);
              }
        }
        onSkipForward={
          isLiveStream
            ? null
            : () => {
                skipForward(15);
              }
        }
        speedLabel={`${playbackRate}× speed`}
        onCycleSpeed={cycleSpeed}
      />
    </div>
  );
}
